import { useNavigate } from 'react-router-dom';
import { useABTest } from '../../../hooks/useABTest';

interface ABTestHeroProps {
  onCTAClick: () => void;
}

export const ABTestHero = ({ onCTAClick }: ABTestHeroProps) => {
  const { variant } = useABTest('term-life-hero');
  const navigate = useNavigate();

  const variantA = {
    headline: 'Term Life Insurance — Protect Your Family for Less in Indianapolis',
    subheadline: "Protect your family's future with coverage that fits your budget. Get a free quote in minutes — no pressure, no surprises.",
    ctaText: 'Get a Free Quote',
    ctaIcon: 'ri-file-list-3-line',
  };

  const variantB = {
    headline: 'Compare Term Life Rates from 20+ Carriers — Indianapolis, IN',
    subheadline: 'No games, no pressure — just honest rates from top carriers. Calculate your personalized rate in under 60 seconds.',
    ctaText: 'Calculate My Rate Free',
    ctaIcon: 'ri-calculator-line',
  };

  const content = variant === 'A' ? variantA : variantB;

  return (
    <>
      <section
        id="term-life-hero"
        className="pt-[90px] md:pt-[120px] pb-12 md:pb-[100px] bg-gradient-to-br from-[#0F2A3F] via-[#1A3A52] to-[#0D2235] relative overflow-hidden"
      >
        {/* Decorative glows — matching homepage */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D97642]/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/4"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#D97642]/8 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4"></div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">

            {/* Left — Headline, badges, image, CTAs */}
            <div className="flex flex-col gap-6">
              <div>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#D97642]/20 text-[#F0A070] text-[12px] font-semibold uppercase tracking-widest rounded-full mb-4 border border-[#D97642]/30">
                  <i className="ri-map-pin-fill text-[13px]" aria-hidden="true"></i>
                  Serving Indianapolis &amp; All of Indiana
                </span>

                <h1 className="text-[34px] sm:text-[44px] md:text-[52px] font-bold text-white leading-[1.15] mb-5">
                  {content.headline}
                </h1>
                <p className="text-[16px] sm:text-[18px] text-white/75 leading-[1.7]">
                  {content.subheadline}
                </p>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap gap-3">
                {[
                  { icon: 'ri-shield-check-fill', label: 'A-Rated Carriers' },
                  { icon: 'ri-lock-line', label: '100% Private & Secure' },
                  { icon: 'ri-map-pin-line', label: 'Local Indianapolis Agent' },
                ].map((b) => (
                  <div key={b.label} className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg border border-white/20 backdrop-blur-sm">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${b.icon} text-[15px] text-[#D97642]`} aria-hidden="true"></i>
                    </div>
                    <span className="text-[13px] text-white/90 font-medium">{b.label}</span>
                  </div>
                ))}
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/15 rounded-lg border border-emerald-500/30 backdrop-blur-sm">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-time-fill text-[15px] text-emerald-400" aria-hidden="true"></i>
                  </div>
                  <span className="text-[13px] text-emerald-300 font-semibold">Free, No Obligation Quote</span>
                </div>
              </div>

              {/* Hero image — visible card, just like homepage */}
              <div className="relative hidden lg:block w-full h-[320px]">
                <div className="absolute inset-0 bg-gradient-to-br from-[#D97642]/20 to-transparent rounded-[24px]"></div>
                <img
                  src="https://public.readdy.ai/ai/img_res/4319f586744aa6f61be18a209026a17d.jpg"
                  alt="Indianapolis Family Reviewing Term Life Insurance — Hoosier Life Insurance"
                  title="Term Life Insurance for Indianapolis Families — Hoosier Life Insurance"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  width={800}
                  height={500}
                  className="w-full h-[320px] object-cover object-top rounded-[24px] shadow-[0_12px_40px_rgba(0,0,0,0.30)] relative z-10"
                />
              </div>

              {/* Primary CTA */}
              <div>
                <button
                  onClick={onCTAClick}
                  className="inline-flex items-center gap-3 px-7 py-4 bg-[#D97642] text-white text-[15px] font-semibold rounded-xl hover:bg-[#C26638] transition-all hover:shadow-xl shadow-md cursor-pointer whitespace-nowrap group"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${content.ctaIcon} text-[20px] text-white`} aria-hidden="true"></i>
                  </div>
                  {content.ctaText}
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-[18px] group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                  </div>
                </button>
                <p className="mt-2.5 text-[13px] text-white/50">
                  <i className="ri-time-line mr-1" aria-hidden="true"></i>15 minutes · No obligation · 100% free
                </p>
              </div>
            </div>

            {/* Right — Why Term Life card */}
            <div className="bg-white rounded-[24px] shadow-[0_8px_48px_rgba(0,0,0,0.25)] p-7 md:p-9 border border-white/10">
              <div className="mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#D97642]/10 text-[#D97642] text-[12px] font-semibold uppercase tracking-wider rounded-full mb-4 border border-[#D97642]/20">
                  <i className="ri-shield-star-line text-[13px]" aria-hidden="true"></i>
                  Why Hoosier Life Insurance?
                </div>
                <h2 className="text-[22px] font-bold text-[#1A3A52] mb-2">Get the Right Coverage at the Right Price</h2>
                <p className="text-[13px] text-[#718096]">We shop 20+ top-rated carriers so you never overpay.</p>
              </div>

              <div className="space-y-4 mb-7">
                {[
                  { icon: 'ri-user-heart-line', title: 'Local Indianapolis Agent', desc: 'Real person, real advice — not a call center.' },
                  { icon: 'ri-shield-check-line', title: 'A-Rated Carrier Network', desc: 'Prudential, Mutual of Omaha, Nationwide & more.' },
                  { icon: 'ri-price-tag-3-line', title: 'Rates From $12/Month', desc: 'Healthy 30-year-old, $500K, 20-year term.' },
                  { icon: 'ri-timer-line', title: 'Quote in Minutes', desc: 'No lengthy forms — get real numbers fast.' },
                  { icon: 'ri-heart-pulse-line', title: 'High-Risk Specialists', desc: 'Diabetics, smokers, pilots — we find coverage others won\'t.' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#D97642]/10 rounded-xl shrink-0">
                      <i className={`${item.icon} text-[20px] text-[#D97642]`} aria-hidden="true"></i>
                    </div>
                    <div>
                      <div className="text-[14px] font-bold text-[#1A3A52]">{item.title}</div>
                      <div className="text-[13px] text-[#718096]">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate('/wealth-strategy-call')}
                className="w-full py-3.5 bg-[#1A3A52] text-white text-[15px] font-bold rounded-xl hover:bg-[#14304A] transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-calendar-check-fill text-[16px]" aria-hidden="true"></i>
                Book a Free Strategy Call
              </button>
              <p className="text-[11px] text-[#718096] text-center mt-3 leading-[1.6]">
                15 minutes · No pressure · Local Indianapolis expert
              </p>
            </div>

          </div>
        </div>

      </section>

      {/* Carrier trust strip — Variant B only */}
      {variant === 'B' && (
        <section className="bg-white py-10 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6">
            <p className="text-center text-sm font-semibold text-gray-500 uppercase tracking-wider mb-6">
              Trusted Carriers We Work With
            </p>
            <div className="flex flex-wrap items-center justify-center gap-10 opacity-60">
              {['Prudential', 'Mutual of Omaha', 'Nationwide', 'Transamerica', 'AIG'].map((carrier) => (
                <div key={carrier} className="text-gray-700 font-bold text-xl">{carrier}</div>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};
